import { useEffect } from 'react'

import Button from '@space-core/components/button'
import Loader from '@space-core/components/loader'

import { Actions, Entities, EventNames, ScreenNames } from '@space-core/datahub'
import useTranslation from '@space-core/hooks/useTranslation'
import { useMembershipStore } from '@space-shell/core/store'
import useCommunityConfigurationSidebar from '@space-shell/modules/communities/communityConfigurationSidebar/hooks/useConfigurationCommunitySidebar/useCommunityConfigurationSidebar'
import { useGetCourses } from '@space-shell/modules/communities/hooks/useGetCourses'
import useSendDataHubConsume from '@space-shell/modules/communities/hooks/useSendDataHubConsume/useSendDataHubConsume'

import { ButtonContainer, CardContainerCourse, ContainerCoursesList } from './styles'
import type { CoursesProps } from './types'

import CourseHeader from '../courseHeader'
import CourseItems from '../courseitems'

const Courses = ({ isOpen }: CoursesProps) => {
  const { sendDataHubConsume } = useSendDataHubConsume()
  const { t } = useTranslation()
  const { membershipSlug } = useMembershipStore()
  const { community } = useCommunityConfigurationSidebar()

  const {
    data,
    isLoading,
    isError,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch,
    isFetching
  } = useGetCourses(membershipSlug, community.id.toString())

  useEffect(() => {
    if (isOpen) refetch()
  }, [isOpen, refetch])

  if (isLoading || (isFetching && !isFetchingNextPage)) {
    return (
      <div className='_mt-7'>
        <CourseHeader />
        <Loader full />
      </div>
    )
  }

  if (isError) {
    return (
      <div className='_mt-7'>
        <CourseHeader />
        <p className='_font-weight _text-gray-900 _mt-7 _text-center'>
          {t('configuration_sidebar.access_and_privacy.error_state')}
        </p>
      </div>
    )
  }

  if (!isLoading && data?.pages[0].content.length === 0) {
    return (
      <div className='_mt-7'>
        <CourseHeader />
        <p className='_font-weight _text-gray-900 _mt-7 _text-center'>
          {t('configuration_sidebar.access_and_privacy.empty_state')}
        </p>
      </div>
    )
  }

  const handleClickSeeMore = () => {
    sendDataHubConsume({
      entity: Entities.COMMUNITY,
      action: Actions.SETUP,
      event: {
        eventName: EventNames.SEE_MORE_PRODUCTS_ACCESS_AND_PRIVACY,
        screenName: ScreenNames.COMMUNITY_CONFIGURATION_MODAL_ACCESS_AND_PRIVACY,
        communityId: community.id,
        communityName: community.name
      }
    })

    fetchNextPage()
  }

  return (
    <div className='_w-full _h-full _mt-7 _mb-7'>
      <CourseHeader />

      <ContainerCoursesList hasNextPage={hasNextPage ?? false}>
        {data?.pages?.map((page) =>
          page?.content?.map((course) => (
            <CardContainerCourse key={course.idProduct}>
              <CourseItems
                backgroundImageFinal={course.backgroundImageFinal}
                hasCommunityAssociated={course.hasCommunityAssociated}
                numberOfAssociatedProducts={page.numberOfAssociatedProducts}
                description={course.description}
                productName={course.productName}
                idProduct={course.idProduct}
              />
            </CardContainerCourse>
          ))
        )}
      </ContainerCoursesList>
      {hasNextPage && (
        <ButtonContainer className='_position-absolute _w-full _d-flex _justify-content-center'>
          <Button
            variation='secondary'
            disabled={isFetchingNextPage}
            onClick={handleClickSeeMore}
            loading={isFetchingNextPage}>
            {t('buttons.show_more')}
          </Button>
        </ButtonContainer>
      )}
    </div>
  )
}

export default Courses
